import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { FormEvent, useState } from "react";
import { validate } from "email-validator";

import { Button } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cellConfig, lookupCellByEmail, setActiveCellId } from "@/lib/cell";
import useDeflowSessionStore, {
  DeflowSessionStates,
} from "@/stores/useDeflowSessionStore";
import { useHostAdapter } from "@/clients/host/adapter";

export const Route = createFileRoute("/plugin/figma/sign-in")({
  component: SignIn,
});

interface CustomElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface CustomForm extends HTMLFormElement {
  readonly elements: CustomElements;
}

function SignIn() {
  const { sessionState, setAuthenticated, setAuthenticationError } =
    useDeflowSessionStore();
  const adapter = useHostAdapter();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);

  const onSubmit = async (event: FormEvent<CustomForm>) => {
    event.preventDefault();

    setIsLoading(true);
    const email = event.currentTarget.elements.email.value;
    const password = event.currentTarget.elements.password.value;

    // Resolve the user's home cell before talking to Cognito so we hit the
    // pool that actually contains them. If the lookup fails (network error,
    // or the directory hasn't replicated their record yet) we fall through
    // to the active cell — typically the US default — and Cognito will
    // surface the usual "user not found" error.
    const lookedUp = await lookupCellByEmail(email);
    if (lookedUp) {
      setActiveCellId(lookedUp);
    }

    const cell = cellConfig();
    const userPool = new CognitoUserPool({
      UserPoolId: cell.userPoolId,
      ClientId: cell.userPoolClientId,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (cognitoSession: CognitoUserSession) => {
        const accessToken = cognitoSession.getAccessToken();
        const idToken = cognitoSession.getIdToken();
        const refreshToken = cognitoSession.getRefreshToken();
        const isValid = cognitoSession.isValid();

        if (!isValid) {
          setIsLoading(false);
          setAuthenticationError();
          return;
        }

        const tokens = {
          accessToken: accessToken.getJwtToken(),
          idToken: idToken.getJwtToken(),
          refreshToken: refreshToken.getToken(),
        };

        const now = Date.now();
        void adapter.persistDeflowSession(tokens, now);

        setIsLoading(false);
        setAuthenticated({ lastSignIn: now, tokens });

        navigate({ to: "/plugin/figma", replace: true });
      },
      onFailure: () => {
        setIsLoading(false);
        setAuthenticationError();
      },
    });
  };

  return (
    <div className="p-8">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm text-muted-foreground">Hello, welcome back.</p>
        </div>
        <div className={"grid gap-6"}>
          <form onSubmit={onSubmit}>
            <div className="grid gap-2">
              <div className="grid gap-1">
                <Label className="sr-only" htmlFor="email">
                  Email
                </Label>
                <Input
                  id="email"
                  placeholder="Email"
                  type="email"
                  autoFocus={true}
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                  onChange={(event) => {
                    setIsEmailValid(validate(event.target.value));
                  }}
                />
                <Label className="sr-only" htmlFor="password">
                  Password
                </Label>
                <Input
                  id="password"
                  placeholder="Password"
                  type="password"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                  onChange={(event) => {
                    setIsPasswordValid(event.target.value.length > 3);
                  }}
                />
              </div>
              {sessionState === DeflowSessionStates.AuthenticationError && (
                <p className="text-sm text-muted-foreground">
                  Could not sign in, email or password might be incorrect.
                </p>
              )}
              <Button disabled={isLoading || !isEmailValid || !isPasswordValid}>
                {isLoading && (
                  <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                Sign in
              </Button>
            </div>
          </form>
        </div>
        <p className="px-8 text-center text-sm text-muted-foreground">
          {`Don't haven an account?`}{" "}
          <Link
            to="/plugin/figma/sign-up"
            className="underline underline-offset-4 hover:text-primary"
          >
            Sign up
          </Link>{" "}
        </p>
      </div>
    </div>
  );
}
